'use client';

/**
 * 使用音频管理器的 Hook
 */
import { useRef, useEffect, useCallback } from 'react';
import { UnifiedAudioManager, getAudioManager } from '@/features/audio';
import type { PlayOptions } from '@/features/audio/types';
import { isSafariBrowser } from '@/utils/browser';

export interface UseAudioManagerReturn {
  /** 音频管理器实例 */
  audioManager: UnifiedAudioManager;
  /** 播放音乐 */
  playMusic: (id: string, options?: PlayOptions) => Promise<void>;
  /** 停止音乐 */
  stopMusic: () => void;
  /** 暂停音乐 */
  pauseMusic: () => void;
  /** 恢复音乐 */
  resumeMusic: () => void;
  /** 淡入播放音乐 */
  fadeInMusic: (id: string, duration?: number) => Promise<void>;
  /** 淡出停止音乐 */
  fadeOutMusic: (duration?: number, callback?: () => void) => void;
  /** 播放音效 */
  playSound: (id: string, options?: PlayOptions) => Promise<void>;
  /** 循环播放音效 */
  playLoopingSound: (id: string, uniqueKey: string, options?: PlayOptions) => Promise<void>;
  /** 快速切换循环音效（先播放新的，再停止旧的，减少切换间隙） */
  switchLoopingSound: (newId: string, uniqueKey: string, options?: PlayOptions) => Promise<void>;
  /** 停止循环音效 */
  stopLoopingSound: (uniqueKey: string) => void;
  /** 停止所有音效 */
  stopAllSounds: () => void;
  /** 设置音乐开关 */
  setMusicEnabled: (enabled: boolean) => void;
  /** 设置音效开关 */
  setSoundEffectEnabled: (enabled: boolean) => void;
  /** 设置音乐音量 */
  setMusicVolume: (volume: number) => void;
  /** 设置音效音量 */
  setSoundEffectVolume: (volume: number) => void;
  /** 获取音乐开关状态 */
  getMusicEnabled: () => boolean;
  /** 获取音效开关状态 */
  getSoundEffectEnabled: () => boolean;
}

/**
 * 使用音频管理器的 Hook
 */
export function useAudioManager(): UseAudioManagerReturn {
  const audioManagerRef = useRef<UnifiedAudioManager | null>(null);

  // 初始化音频管理器
  useEffect(() => {
    if (!audioManagerRef.current) {
      audioManagerRef.current = getAudioManager();
    }

    return () => {
      // 组件卸载时不销毁管理器（因为可能是全局单例）
      // 如果需要销毁，可以调用 resetAudioManager()
    };
  }, []);

  // 确保 audioManager 已经初始化
  if (!audioManagerRef.current) {
    audioManagerRef.current = getAudioManager({
      autoUnlock: isSafariBrowser(),
    });
  }

  const audioManager = audioManagerRef.current;

  const playMusic = useCallback(async (id: string, options?: PlayOptions) => {
    return audioManager.musicManager.playMusic(id, options);
  }, [audioManager]);

  const stopMusic = useCallback(() => {
    audioManager.musicManager.stopMusic();
  }, [audioManager]);

  const pauseMusic = useCallback(() => {
    audioManager.musicManager.pauseMusic();
  }, [audioManager]);

  const resumeMusic = useCallback(() => {
    audioManager.musicManager.resumeMusic();
  }, [audioManager]);

  const fadeInMusic = useCallback(async (id: string, duration?: number) => {
    return audioManager.musicManager.fadeInMusic(id, duration);
  }, [audioManager]);

  const fadeOutMusic = useCallback((duration?: number, callback?: () => void) => {
    audioManager.musicManager.fadeOutMusic(duration, callback);
  }, [audioManager]);

  const playSound = useCallback(async (id: string, options?: PlayOptions) => {
    return audioManager.soundEffectManager.playSound(id, options);
  }, [audioManager]);

  const playLoopingSound = useCallback(async (id: string, uniqueKey: string, options?: PlayOptions) => {
    return audioManager.soundEffectManager.playLoopingSound(id, uniqueKey, options);
  }, [audioManager]);

  const switchLoopingSound = useCallback(async (newId: string, uniqueKey: string, options?: PlayOptions) => {
    return audioManager.soundEffectManager.switchLoopingSound(newId, uniqueKey, options);
  }, [audioManager]);

  const stopLoopingSound = useCallback((uniqueKey: string) => {
    audioManager.soundEffectManager.stopLoopingSound(uniqueKey);
  }, [audioManager]);

  const stopAllSounds = useCallback(() => {
    audioManager.soundEffectManager.stopAllSounds();
  }, [audioManager]);

  const setMusicEnabled = useCallback((enabled: boolean) => {
    audioManager.audioManager.setMusicEnabled(enabled);
    audioManager.updateVolume();
  }, [audioManager]);

  const setSoundEffectEnabled = useCallback((enabled: boolean) => {
    audioManager.audioManager.setSoundEffectEnabled(enabled);
    audioManager.updateVolume();
  }, [audioManager]);

  const setMusicVolume = useCallback((volume: number) => {
    audioManager.audioManager.setMusicVolume(volume);
    audioManager.updateVolume();
  }, [audioManager]);

  const setSoundEffectVolume = useCallback((volume: number) => {
    audioManager.audioManager.setSoundEffectVolume(volume);
    audioManager.updateVolume();
  }, [audioManager]);

  const getMusicEnabled = useCallback(() => {
    return audioManager.audioManager.getMusicEnabled();
  }, [audioManager]);

  const getSoundEffectEnabled = useCallback(() => {
    return audioManager.audioManager.getSoundEffectEnabled();
  }, [audioManager]);

  return {
    audioManager,
    playMusic,
    stopMusic,
    pauseMusic,
    resumeMusic,
    fadeInMusic,
    fadeOutMusic,
    playSound,
    playLoopingSound,
    switchLoopingSound,
    stopLoopingSound,
    stopAllSounds,
    setMusicEnabled,
    setSoundEffectEnabled,
    setMusicVolume,
    setSoundEffectVolume,
    getMusicEnabled,
    getSoundEffectEnabled,
  };
}

